import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Search, X } from 'lucide-react';
import { profilePublicPath } from '../../lib/profileRoutes';
import {
  addSearchHistoryEntry,
  clearSearchHistory,
  loadSearchHistory,
} from '../../lib/searchHistory';
import { globalSearch, type SearchResults } from '../../services/searchService';
import { productPath } from '../../lib/seo/routes';

interface GlobalSearchOverlayProps {
  open: boolean;
  onClose: () => void;
  placeholder?: string;
}

export const GlobalSearchOverlay: React.FC<GlobalSearchOverlayProps> = ({
  open,
  onClose,
  placeholder = 'Search users, listings, posts...',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>(loadSearchHistory);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSearchResults(null);
      return;
    }
    searchInputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    const timeout = window.setTimeout(async () => {
      setSearching(true);
      const results = await globalSearch(searchQuery, 8);
      setSearchResults(results);
      setSearching(false);

      const hasAny =
        results.profiles.length > 0 || results.listings.length > 0 || results.posts.length > 0;
      if (hasAny) {
        setSearchHistory((prev) => addSearchHistoryEntry(searchQuery, prev));
      }
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  const handleResultNavigate = () => {
    const q = searchQuery.trim();
    if (q.length >= 2) {
      setSearchHistory((prev) => addSearchHistoryEntry(q, prev));
    }
    onClose();
  };

  const hasResults =
    searchResults &&
    (searchResults.profiles.length > 0 ||
      searchResults.listings.length > 0 ||
      searchResults.posts.length > 0);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="fixed top-0 left-0 right-0 z-[101] bg-district-surface border-b border-neutral-800 shadow-2xl pt-[calc(3.5rem+env(safe-area-inset-top,0px))]"
          >
            <div className="max-w-2xl mx-auto p-4">
              <div className="relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  ref={searchInputRef}
                  type="search"
                  enterKeyHint="search"
                  placeholder={placeholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-12 pr-16 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                />
                {searchQuery.trim() ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults(null);
                      searchInputRef.current?.focus();
                    }}
                    className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors p-1"
                    title="Clear search"
                  >
                    <X size={18} />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={onClose}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors p-1"
                  title="Close search"
                >
                  <X size={18} />
                </button>
                {searching && (
                  <Loader2
                    size={18}
                    className="absolute right-9 top-1/2 -translate-y-1/2 text-gray-500 animate-spin"
                  />
                )}
              </div>

              {searchHistory.length > 0 && searchQuery.trim().length < 2 ? (
                <div className="mt-3">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Recent</p>
                    <button
                      type="button"
                      onClick={() => {
                        clearSearchHistory();
                        setSearchHistory([]);
                      }}
                      className="text-[11px] text-gray-500 hover:text-white"
                    >
                      Clear history
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {searchHistory.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => setSearchQuery(q)}
                        className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 bg-black/30 text-gray-300 hover:text-white hover:border-white/25"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {hasResults && (
                <div className="mt-4 space-y-4 max-h-96 overflow-y-auto">
                  {searchResults!.profiles.length > 0 && (
                    <div>
                      <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Users</h4>
                      {searchResults!.profiles.map((p) => (
                        <Link
                          key={p.id}
                          to={profilePublicPath(p)}
                          onClick={handleResultNavigate}
                          className="flex items-center gap-3 p-2 hover:bg-gray-800 rounded-lg transition-colors"
                        >
                          <div className="w-8 h-8 bg-gray-700 rounded-full overflow-hidden">
                            {p.avatar_url ? (
                              <img src={p.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="flex items-center justify-center h-full text-xs font-bold text-gray-400">
                                {p.username.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium">@{p.username}</div>
                            {p.display_name?.trim() && p.display_name.trim() !== p.username && (
                              <div className="text-xs text-gray-500 truncate">{p.display_name}</div>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}

                  {searchResults!.listings.length > 0 && (
                    <div>
                      <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Listings</h4>
                      {searchResults!.listings.map((l) => (
                        <Link
                          key={l.id}
                          to={productPath(l)}
                          onClick={handleResultNavigate}
                          className="flex items-center gap-3 p-2 hover:bg-gray-800 rounded-lg transition-colors"
                        >
                          <div className="w-8 h-8 bg-gray-700 rounded-lg overflow-hidden">
                            {(l.images as string[])?.[0] && (
                              <img
                                src={(l.images as string[])[0]}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{l.title}</div>
                            <div className="text-xs text-gray-500">
                              ${Number(l.price)} · {l.condition}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}

                  {searchResults!.posts.length > 0 && (
                    <div>
                      <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Posts</h4>
                      {searchResults!.posts.map((p) => (
                        <Link
                          key={p.id}
                          to={`/post/${p.id}`}
                          onClick={handleResultNavigate}
                          className="block p-2 hover:bg-gray-800 rounded-lg transition-colors"
                        >
                          <div className="text-sm truncate">{p.content}</div>
                          <div className="text-xs text-gray-500">by @{p.author?.username}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {searchQuery.length >= 2 && !searching && !hasResults && (
                <div className="text-center py-6 text-gray-500 text-sm">No results found</div>
              )}
              {searchQuery.length > 0 && searchQuery.length < 2 && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  Type at least 2 characters to search
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
